import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import {
  confirmMembershipRenewal, getMembershipRenewalStatus, previewMembershipRenewal,
  MembershipRenewalOperation, MembershipRenewalPreview
} from '../api/API';
import moment from 'moment';
import './MembershipRenewal.css';

const formatDate = (value: string) => moment.parseZone(value).format('MMMM D, YYYY');
const formatPlanName = (value: string) => value.replace(/\s+\(standard\)$/i, '');
const formatAmount = (quote: MembershipRenewalPreview) => new Intl.NumberFormat('en-IE', {
  style: 'currency', currency: quote.currency
}).format(quote.amount_cents / 100);

interface Props {
  userId: number;
  enabled: boolean;
  eligible: boolean;
  nextBillingAt?: string;
  onRenewed: () => Promise<void>;
}

const storagePrefix = (userId: number) => `membership-renewal:${userId}`;
const storageKey = (userId: number, requestId: string) => `${storagePrefix(userId)}:${requestId}`;
const restore = (userId: number): MembershipRenewalPreview | null => {
  try {
    // Each tab owns only its operation's record. Also read the earlier per-user
    // key so an in-flight payment survives deployment of this version.
    const prefix = storagePrefix(userId);
    const keys = [prefix, ...Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index))
      .filter((key): key is string => !!key && key.startsWith(`${prefix}:`))];
    for (const key of keys) {
      try {
        const value = JSON.parse(localStorage.getItem(key) || 'null');
        if (value?.request_id && value?.confirmation_token) return value;
      } catch { /* Skip a malformed record without losing other operations. */ }
    }
    return null;
  } catch { return null; }
};

export default function MembershipRenewal({ userId, enabled, eligible, nextBillingAt, onRenewed }: Props) {
  const [preview, setPreview] = useState<MembershipRenewalPreview | null>(null);
  const [pending, setPending] = useState(() => restore(userId));
  const [operationStatus, setOperationStatus] = useState('processing');
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const resolvedOperation = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const refresh = useRef(onRenewed);
  refresh.current = onRenewed;

  const clearPending = (requestId: string) => {
    try {
      localStorage.removeItem(storageKey(userId, requestId));
      const legacyKey = storagePrefix(userId);
      if (JSON.parse(localStorage.getItem(legacyKey) || 'null')?.request_id === requestId) localStorage.removeItem(legacyKey);
    } catch { /* A replay still resolves to the same operation. */ }
    setPending(current => current?.request_id === requestId ? restore(userId) : current);
  };

  const acceptResult = async (result: MembershipRenewalOperation) => {
    if (result.request_id && resolvedOperation.current === result.request_id) return;
    setOperationStatus(result.status);
    if (result.status === 'processed') {
      resolvedOperation.current = result.request_id;
      clearPending(result.request_id);
      setPreview(null);
      setError(null);
      setMessage('Your membership has been renewed.');
      try { await refresh.current(); }
      catch { setError('Your renewal succeeded. Please refresh the page to see your updated balance.'); }
    } else if (result.status === 'failed') {
      resolvedOperation.current = result.request_id;
      clearPending(result.request_id);
      setPreview(null);
      setError(result.message || 'Your saved card could not be charged. Please update your card and try again.');
    }
  };

  useEffect(() => {
    const syncRecovery = (event: StorageEvent) => {
      const prefix = storagePrefix(userId);
      if (event.key && (event.key === prefix || event.key.startsWith(`${prefix}:`)) && event.newValue && !submitting.current) {
        setPending(current => current || restore(userId));
        setPreview(null);
      }
    };
    window.addEventListener('storage', syncRecovery);
    return () => window.removeEventListener('storage', syncRecovery);
  }, [userId]);

  useEffect(() => {
    if (!pending) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const check = async () => {
      try {
        const result = await getMembershipRenewalStatus(userId, pending.request_id);
        if (!active) return;
        await acceptResult(result);
        if (result.status === 'processed' || result.status === 'failed') return;
      } catch {
        if (active) setError('We are checking your payment. Please do not start another renewal.');
      }
      if (active) timer = setTimeout(check, 5000);
    };
    void check();
    return () => { active = false; clearTimeout(timer); };
    // The operation ID is stable across retries; refresh uses the latest callback.
  }, [pending?.request_id, userId]);

  const openPreview = async () => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true); setError(null); setMessage(null);
    try { setPreview(await previewMembershipRenewal(userId)); }
    catch (e: any) { setError(e.message || 'Unable to load renewal details. Please try again.'); }
    finally { submitting.current = false; setBusy(false); }
  };

  const pay = async (quote: MembershipRenewalPreview) => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true); setError(null);
    try {
      // Persist before charging so a reload can recover this exact operation.
      localStorage.setItem(storageKey(userId, quote.request_id), JSON.stringify(quote));
    } catch {
      setError('Please enable browser storage before making a payment.');
      submitting.current = false; setBusy(false);
      return;
    }
    setPending(quote);
    setOperationStatus('processing');
    try {
      const result = await confirmMembershipRenewal(userId, quote);
      if (result.status === 'processing') setPreview(null);
      await acceptResult(result);
    }
    catch (e: any) {
      if (resolvedOperation.current === quote.request_id) return;
      if (e.status === 422 && e.code === 'MEMBERSHIP_RENEWAL_UNAVAILABLE') {
        clearPending(quote.request_id); setPreview(null);
        setError(e.message || 'Please review your renewal details again.');
      } else {
        setPreview(null);
        setError('We are checking your payment. Please do not start another renewal.');
      }
    } finally { submitting.current = false; setBusy(false); }
  };

  if (!enabled && !pending && !message && !error) return null;
  return <div className="pm-renewal-control">
    {enabled && eligible && !pending && <section className="pm-renewal-entry" aria-label="Early membership renewal">
      <div className="pm-renewal-entry-copy">
        <h2>Need more coins?</h2>
        <p>Renew early and receive your next package immediately.</p>
        <p className="pm-renewal-entry-date">Next automatic renewal: <strong>{nextBillingAt ? formatDate(nextBillingAt) : 'Unavailable'}</strong></p>
      </div>
      <button type="button" className="pm-primary-button" disabled={busy} onClick={openPreview}>
        {busy ? 'Loading...' : 'Renew Membership Now'}
      </button>
    </section>}
    {pending && <div className="pm-renewal-notice" role="status">
      <p>Your renewal is being checked. Please do not start another payment.</p>
      {operationStatus === 'not_found' && <button type="button" className="pm-secondary-button"
        disabled={busy} onClick={() => pay(pending)}>Resume renewal</button>}
    </div>}
    {!preview && error && <p className="pm-renewal-notice pm-renewal-error" role="alert">{error}</p>}
    {message && <p className="pm-renewal-notice pm-renewal-success" role="status">{message}</p>}
    <Modal show={!!preview} onHide={() => { if (!busy && !pending) setPreview(null); }}
      backdrop={busy || pending ? 'static' : true} keyboard={!busy && !pending}
      className="pm-renewal-modal" dialogClassName="pm-renewal-dialog"
      aria-labelledby="membership-renewal-title" aria-describedby="membership-renewal-description" centered scrollable>
      <Modal.Header><Modal.Title as="h2" id="membership-renewal-title">Renew your membership now?</Modal.Title></Modal.Header>
      <Modal.Body>
        {error && <p className="pm-renewal-notice pm-renewal-error" role="alert">{error}</p>}
        {preview && <>
        <p className="pm-renewal-intro" id="membership-renewal-description">Receive your next package now using your saved card.</p>
        <div className="pm-renewal-purchase">
          <div className="pm-renewal-plan"><span>Membership</span><strong>{formatPlanName(preview.plan)}</strong></div>
          <div className="pm-renewal-amount"><span>Charge today</span><strong>{formatAmount(preview)}</strong></div>
        </div>
        <div className="pm-renewal-balances">
          {preview.coins.map(coins => <section className="pm-renewal-balance" key={coins.kind}
            aria-label={`${coins.kind === 'private' ? 'Private' : 'Dance'} coin balance`}>
            <h3>{coins.kind === 'private' ? 'Private' : 'Dance'} Coins</h3>
            <dl>
              <div><dt>New coins</dt><dd>{coins.allocation.toLocaleString()}</dd></div>
              <div><dt>Coins carried over</dt><dd>{coins.carried.toLocaleString()}</dd></div>
              <div className="pm-renewal-coin-total"><dt>Balance after renewal</dt><dd>{coins.projected_balance.toLocaleString()}</dd></div>
            </dl>
            {coins.forfeited > 0 && <p className="pm-renewal-loss" role="alert">{coins.forfeited.toLocaleString()} {coins.kind} coins will not carry over.</p>}
          </section>)}
        </div>
        <dl className="pm-renewal-billing">
          <div><dt>Next automatic renewal</dt><dd>{formatDate(preview.next_billing_at)}</dd></div>
        </dl>
        <p className="pm-renewal-policy">{preview.rollover_notice}</p>
        <p className="pm-renewal-payment-note">Your saved card will be charged when you confirm.</p>
      </>}</Modal.Body>
      <Modal.Footer>
        <button type="button" className="pm-secondary-button" disabled={busy || !!pending} onClick={() => setPreview(null)}>Cancel</button>
        <button type="button" className="pm-primary-button" disabled={busy || !!pending || !preview}
          onClick={() => preview && pay(preview)}>{busy || pending ? 'Processing...' : `Confirm & Pay${preview ? ` ${formatAmount(preview)}` : ''}`}</button>
      </Modal.Footer>
    </Modal>
  </div>;
}
